import {call, put, select, take, takeEvery, takeLatest, takeLeading} from 'redux-saga/effects'
import isFunction from 'lodash/isFunction'
import noop from 'lodash/noop'

import {
  LOGIN_SUCCESS,
  PASSWORD_RESET_REQUEST_FAILURE,
  PASSWORD_RESET_REQUEST_SUCCESS,
  REGISTRATION_FAILURE,
  REGISTRATION_SUCCESS,
  login,
  passwordResetRequest,
  registration,
} from '../../../actions/api'
import {UPDATE_CURRENT_OWNER_ROLE} from '../../../actions/events'
import {
  CLOSE_ONBOARDING,
  COMPLETE_ONBOARDING,
  GO_BACKWARDS,
  GO_FORWARDS,
  LOGIN_VIA_ONBOARDING,
  OPEN_ONBOARDING,
  REGISTER_VIA_ONBOARDING,
  RESET_PASSWORD_REQUEST_VIA_ONBOARDING,
  changeStep,
  closeOnboarding,
  goForwards,
  showOnboardingPasswordResetRequestSuccess,
} from './actions'
import {activateNewAccountHellobar} from '../../../actions/helloBar'
import {openPartyAvailableModal, openSoldOutModal} from '../../../actions/productDetailPage'
import {registrationFinished} from '../../../actions/user'
import * as api from '../../../api/api'
import * as cross from '../../../store/cross'
import {OnboardingSteps, OnboardingStepNames} from '../../../store/enum'
import * as events from '../../../store/events'
import * as onboardingSelectors from './store'
import * as track from '../../../track/track'
import * as URLS from '../../../urls'

import * as optimizely from '../../../analytics/optimizely'
import {sendOptimizelyEvent} from '../../../track/helpers'

// Open
// ----------------------------------------------------------------------------

export function onboardingOpenWorker() {
  track.onboardingOpened()
  // Required for hidePassword to activate experiment on openedOnboarding
  sendOptimizelyEvent('page', {pageName: 'openedOnboarding'})
}

export function* onboardingOpenSaga() {
  yield takeEvery(OPEN_ONBOARDING, onboardingOpenWorker)
}

// Event Role Selected
// ----------------------------------------------------------------------------

/**
 * The UPDATE_CURRENT_OWNER_ROLE event can be fired in multiple places outside of onboarding
 * (eg: dashboard). To work around this, we're first listening for the openOnboarding action to
 * ensure we're within the onboarding flow. We're using a combination of Optimizely's "Audience Targeting"
 * feature and manual page activation to hopefully bucket users based on their chosen event role. We're
 * making the assumption here that we don't need the event type since the event role question is only
 * surfaced if the user selects the "Wedding" event type.
 */
export function* onboardingEventRoleSelectedWorker() {
  const {payload: eventRole} = yield take(UPDATE_CURRENT_OWNER_ROLE)
  yield call(optimizely.captureAudienceAttributes, 'registeredEventRole', eventRole)
}

export function* onboardingEventRoleSelectedSaga() {
  yield takeLatest(OPEN_ONBOARDING, onboardingEventRoleSelectedWorker)
}
// Registration
// ----------------------------------------------------------------------------

// NOTE: This saga requires user id, event id, and event owner id to work
//  correctly. Why is this a callout? Because it's triggered before
//  USER_AND_EVENT_RECEIVED is fired.

export function* onboardingRegistrationWorker({payload: {email, password}}) {
  // This information must be selected from the store _prior_ to calling the
  // `registration` action because the response from that API call overwrites
  // the `events.current` key in our Redux store and does not contain all the
  // same information
  const eventType = yield select(events.currentType)
  const eventRole = yield select(events.currentOwnerRole)
  const eventDetails = {
    eventType,
    eventDate: yield select(events.currentDate),
    ownerRole: eventRole,
    weddingPartyCount: yield select(events.currentNumMembers),
  }
  yield put(
    registration({
      email,
      eventRole, // for analytics only
      eventType, // for analytics only
      location: 'Onboarding Modal',
      password,
    }),
  )
  const {payload, type} = yield take([REGISTRATION_FAILURE, REGISTRATION_SUCCESS])
  if (type === REGISTRATION_FAILURE) {
    // TODO: investigate whether we should handle registration failures better
    return
  }

  yield call(track.onboardingRegistered, email)

  const userIsParticipant = yield select(cross.userIsParticipant)
  if (userIsParticipant) {
    yield put(closeOnboarding())
    yield put(registrationFinished())
    return
  }

  // save event information from onboarding to server
  try {
    const response = yield call(api.postOnboardingDetails, payload.event.id, eventDetails)
    api.onStatus({
      OK: noop,
      DEFAULT: ({status, statusText}) => {
        throw new Error(`Could not post onboarding event details. Status: ${status} ${statusText}.`)
      },
    })(response)
    yield put(goForwards())
    yield put(registrationFinished())
  } catch (e) {
    yield call(console.error, `Could not complete onboarding registration. Reason: ${e.message}`)
  }
}

export function* onboardingRegistrationSaga() {
  yield takeLeading(REGISTER_VIA_ONBOARDING, onboardingRegistrationWorker)
}

// Password Reset Request
// ----------------------------------------------------------------------------

export function* onboardingPasswordResetRequestWorker({payload: {email}}) {
  try {
    // add the location to the action payload and dispatch
    yield put(passwordResetRequest(email, 'Onboarding Modal'))

    // wait for a failure or success
    const {type} = yield take([PASSWORD_RESET_REQUEST_FAILURE, PASSWORD_RESET_REQUEST_SUCCESS])

    if (type === PASSWORD_RESET_REQUEST_SUCCESS) {
      // TODO: GROWTH-927 decide whether to add.
      // record a successful login through onboarding
      // track.onboardingPasswordReset()

      // show the success message
      yield put(showOnboardingPasswordResetRequestSuccess())
    } else {
      // here's where we might put some custom code to handle password reset errors for Onboarding,
      //  but it looks like the base error code will suffice
    }
  } catch (e) {
    console.error(`Could not complete onboarding password reset. Reason: ${e.message}`)
  }
}

export function* onboardingPasswordResetRequestSaga() {
  yield takeLeading(RESET_PASSWORD_REQUEST_VIA_ONBOARDING, onboardingPasswordResetRequestWorker)
}

function navigateToGuidedLooksPCP(url) {
  window.location.href = url
}

/**
 * Handles actions after onboarding is considered complete. Modals are shown depending two conditions:
 * 1) There are sold out products in the user's event
 * 2) There are products that are not eligible to be rented for a group
 *
 * If there is no defined `onSuccess` callback, a user will be redirected to the Suits & Tuxedos
 * product category page.
 *
 * @returns {IterableIterator<*>}
 */
export function* postOnboardingCompleteWorker() {
  /**
   * This success callback is passed to the initiateOnboardingAction when the addToLook action is called.
   * The success callback will dispatch the addProductToLook action.
   * @see [addToLook.js](https://github.com/TheBlackTuxCorp/jacuzzi/blob/staging/src/js/actions/addToLook.js)
   */
  const onboardingSuccessCallback = yield select(onboardingSelectors.onSuccess)

  if (!isFunction(onboardingSuccessCallback)) {
    yield put(activateNewAccountHellobar(2000))
    yield call(navigateToGuidedLooksPCP, URLS.COLLECTION_TUXEDOS_SUITS)
    return
  }

  const isRentSoldOutForEvent = yield select(cross.isRentSoldOutForEvent)
  if (isRentSoldOutForEvent) {
    yield put(openSoldOutModal())
    return
  }

  const isPartyAvailable = yield select(cross.isPartyAvailable)
  if (!isPartyAvailable) {
    // NOTE: this could be the reason why PartyAvailableModalCta invokes `onboarding.onSuccess()`
    yield put(openPartyAvailableModal())
    return
  }

  const eventId = yield select(events.currentId)
  yield call(onboardingSuccessCallback, eventId)
}

// Onboarding Complete & Attribution Survey Done
// ----------------------------------------------------------------------------

export function* onboardingCompleteWorker() {
  yield put(closeOnboarding())
  yield call(postOnboardingCompleteWorker)
}

export function* onboardingCompleteSaga() {
  yield takeEvery([COMPLETE_ONBOARDING], onboardingCompleteWorker)
}

// Close
// ----------------------------------------------------------------------------

/**
 * If a user closes onboarding at step 7 (the attribution survey)
 * we want to still consider onboarding complete
 * and surface error modals or add products to a look.
 * @returns {IterableIterator<*>}
 */
export function* onboardingCloseWorker() {
  const step = yield select(onboardingSelectors.step)
  yield call(track.onboardingClosed, OnboardingStepNames[step])

  if ([OnboardingSteps.attributionSurvey].includes(step)) {
    yield call(postOnboardingCompleteWorker)
  }
}

export function* onboardingCloseSaga() {
  yield takeEvery(CLOSE_ONBOARDING, onboardingCloseWorker)
}

// Login
// ----------------------------------------------------------------------------

export function* onboardingLoginWorker({payload: {email, password}}) {
  yield put(login(email, 'Onboarding Modal', password))
  yield take(LOGIN_SUCCESS)
  yield call(track.onboardingLoggedIn, email)
  yield call(onboardingCompleteWorker)
}

export function* onboardingLoginSaga() {
  yield takeLatest(LOGIN_VIA_ONBOARDING, onboardingLoginWorker)
}

// Navigation
// ----------------------------------------------------------------------------

export function* goForwardsWorker() {
  const nextStep = yield select(onboardingSelectors.getNextStep)
  yield put(changeStep('forwards', nextStep))
}

export function* goForwardsSaga() {
  yield takeLatest(GO_FORWARDS, goForwardsWorker)
}

export function* goBackwardsWorker() {
  const previousStep = yield select(onboardingSelectors.getPrevStep)
  yield put(changeStep('backwards', previousStep))
}

export function* goBackWardsSaga() {
  yield takeLatest(GO_BACKWARDS, goBackwardsWorker)
}
